import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { patients as patientsApi } from '@/api/endpoints'
import type { ApiError } from '@/api/errors'
import type { Paginated, SearchHitResponse } from '@/api/types'
import { queryKeys } from '@/api/queryKeys'
import { Banner } from '@/components/Banner'
import { Card } from '@/components/Card'
import { Page, PageHeader } from '@/components/Page'
import { EmptyState } from '@/components/EmptyState'
import { Icon } from '@/components/Icon'
import { Skeleton, SkeletonList } from '@/components/Skeleton'
import { TrendChart } from '@/components/TrendChart'
import { useSession } from '@/features/auth/SessionProvider'
import { PatientResultRow } from '@/features/search/PatientResultRow'
import { insights, type DashboardResponse, type FollowUp } from './api'
import { callers as callersApi } from '@/features/callers/api'
import type { CallerResponse } from '@/api/types'
import { StatGrid, StatTile } from './StatTile'
import { Button } from '@/components/Button'
import { useModals } from '@/app/modals/ModalContext'
import { errorMessage } from '@/api/messages'
import { CallerResultRow } from '@/features/callers/CallerResultRow'
import { FollowUpResultRow } from './FollowUpResultRow'

const TODAY = new Intl.DateTimeFormat(undefined, {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
})

/** What the clinic looks like this morning. Every number links to the list
 *  behind it — a dashboard that cannot be drilled into is decoration. */
export function DashboardScreen() {
  const modals = useModals()
  const { user } = useSession()

  const data = useQuery<DashboardResponse, ApiError>({
    queryKey: ['dashboard'],
    queryFn: ({ signal }) => insights.dashboard(signal),
  })
  const due = useQuery<{ data: FollowUp[] }, ApiError>({
    queryKey: ['followups', 'week', 6],
    queryFn: ({ signal }) => insights.followups('week', 6, signal),
  })
  const calls = useQuery<{ data: CallerResponse[] }, ApiError>({
    queryKey: queryKeys.callersDue,
    queryFn: ({ signal }) => callersApi.due(6, signal),
  })
  const recent = useQuery<Paginated<SearchHitResponse>, ApiError>({
    queryKey: queryKeys.patientList(6),
    queryFn: ({ signal }) => patientsApi.recent(6, null, signal),
  })

  const summary = data.data

  const register = (
    <Button onClick={() => modals.open({ kind: 'patient', mode: 'create' })}>
      <Icon name="userPlus" />
      Register new patient
    </Button>
  )

  return (
    <Page action={register}>
      <PageHeader
        title={`Good day${user ? `, ${user.full_name.replace(/^Dr\.?\s*/i, 'Dr ')}` : ''}`}
        description={TODAY.format(new Date())}
        primary={register}
      />

      {data.error && (
        <Banner tone={data.error.isNetwork ? 'warning' : 'danger'}>{errorMessage(data.error)}</Banner>
      )}

      <div className="grid content-start gap-8">
        <StatGrid>
          {!summary ? (
            Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="grid content-start gap-2 bg-canvas px-4 py-3.5" role="status" aria-label="Loading">
                <Skeleton width="65%" height="0.75rem" />
                <Skeleton width="40%" height="2rem" />
                <Skeleton width="50%" height="0.75rem" />
              </div>
            ))
          ) : (
            <>
              <StatTile
                label="Consultations today"
                value={summary.consultations_today}
                icon="file"
                hint={summary.consultations_today === 0 ? 'None recorded yet' : 'Recorded so far'}
              />
              <StatTile
                label="Follow-ups due today"
                value={summary.followups_today}
                icon="calendar"
                to="/appointments?window=today"
              />
              <StatTile
                label="Follow-ups overdue"
                value={summary.followups_overdue}
                icon={summary.followups_overdue > 0 ? 'alert' : 'check'}
                tone={summary.followups_overdue > 0 ? 'warn' : 'good'}
                to="/appointments?window=overdue"
              />
              <StatTile
                label="Patients on file"
                value={summary.patients_total}
                icon="users"
                to="/patients"
              />
              <StatTile
                label="New patients this week"
                value={summary.patients_new_week}
                icon="userPlus"
                hint="Registered in the last 7 days"
              />
              <StatTile
                label="Documents stored"
                value={summary.documents_total}
                icon="image"
                hint="Scans, X-rays and reports"
              />
            </>
          )}
        </StatGrid>

        {/* The only route to these two on a phone: the bottom tab bar carries
            the six sections the brief names, and the sidebar that lists the
            derived views is desktop-only. */}
        <Card
          title="Consultations, last 14 days"
          action={
            <span className="flex items-center gap-4">
              <Link className="inline-flex items-center text-sm font-medium hover:underline" to="/prescriptions">
                Prescriptions
              </Link>
            </span>
          }
        >
          {!summary ? (
            <SkeletonList rows={4} label="Loading consultation trend" />
          ) : (
            <TrendChart
              points={summary.consultations_per_day}
              label="Consultations per day over the last fourteen days"
            />
          )}
        </Card>

        {/* Columns, not cards: each region is marked by the rule under its
            own label, so the only thing needed between them is room. */}
        <div className="grid grid-cols-1 items-start gap-x-10 gap-y-8 md:grid-cols-2 min-[75rem]:grid-cols-3">
          <Card
            title="Due back this week"
            action={
              <Link className="inline-flex items-center text-sm font-medium hover:underline" to="/appointments?window=week">
                All follow-ups
              </Link>
            }
            flush
          >
            {due.isLoading ? (
              <div className="pt-1">
                <SkeletonList rows={3} label="Loading follow-ups" />
              </div>
            ) : (due.data?.data.length ?? 0) === 0 ? (
              <EmptyState
                icon={<Icon name="calendar" size="2rem" />}
                title="Nobody due this week"
                body="Set a next-visit date on a consultation and the patient appears here."
              />
            ) : (
              <ul className="m-0 list-none p-0 [&>li+li]:border-t [&>li+li]:border-line-soft">
                {due.data?.data.map((row) => (
                  <FollowUpResultRow key={`${row.patient_id}-${row.due_on}`} followUp={row} />
                ))}
              </ul>
            )}
          </Card>

          <Card
            title="Calls to return"
            action={
              <Link className="inline-flex items-center text-sm font-medium hover:underline" to="/callers">
                All callers
              </Link>
            }
            flush
          >
            {calls.isLoading ? (
              <div className="pt-1">
                <SkeletonList rows={3} label="Loading calls to return" />
              </div>
            ) : (calls.data?.data.length ?? 0) === 0 ? (
              <EmptyState
                icon={<Icon name="phone" size="2rem" />}
                title="Nobody waiting on a call"
                body="Enquiries with a follow-up date due appear here until someone rings back."
              />
            ) : (
              <ul className="m-0 list-none p-0 [&>li+li]:border-t [&>li+li]:border-line-soft">
                {calls.data?.data.map((caller) => (
                  <CallerResultRow key={caller.id} caller={caller} />
                ))}
              </ul>
            )}
          </Card>

          <Card
            title="Recently seen"
            action={
              <Link className="inline-flex items-center text-sm font-medium hover:underline" to="/patients">
                All patients
              </Link>
            }
            flush
          >
            {recent.isLoading ? (
              <div className="pt-1">
                <SkeletonList rows={3} label="Loading recent patients" />
              </div>
            ) : (recent.data?.data.length ?? 0) === 0 ? (
              <EmptyState
                icon={<Icon name="users" size="2rem" />}
                title="No patients yet"
                body="Register the first patient to start the register."
              />
            ) : (
              <ul className="m-0 list-none p-0 [&>li+li]:border-t [&>li+li]:border-line-soft">
                {recent.data?.data.map((hit) => <PatientResultRow key={hit.id} hit={hit} />)}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </Page>
  )
}
